import { useCallback, useRef, useState, type RefObject } from 'react';
import { Keyboard, TextInput } from 'react-native';
import {
  BottomSheetContainer,
  ColorValues,
  createUseBottomSheetModal,
  Flex,
  PrimaryButton,
} from '@banhala/ui-kit';
import { KeyboardAvoiding } from '../../../KeyboardAvoiding';
import { bridge, delay, isWeb } from '../../../../utils';
import { isAndroid } from '../../../../utils/isAndroid';
import { useAsyncEffect } from '../../../../utils/useAsyncEffect';
import { useLogAnalyticsEvent } from '../../../../hooks/useLogAnalyticsEvent';
import { usePostNonEntryMarkets } from './usePostNonEntryMarkets';
import type { IScreensLogging } from '../../../../api/types/IScreenLogging';

type UseInputNonEntryMarketBottomSheetParam = {
  // 사용자가 검색했던 원본 키워드 (모달 진입 시 prefill + SEARCH_KEYWORD 로깅)
  searchKeyword?: string;
  logging?: IScreensLogging;
};

export const useInputNonEntryMarketBottomSheet = createUseBottomSheetModal<
  void,
  UseInputNonEntryMarketBottomSheetParam
>(
  ({ confirm, cancel, param: { searchKeyword, logging } }) => {
    const [keyword, setKeyword] = useState(searchKeyword ?? '');
    const ref = useRef<TextInput>(null);
    useKeyboardFocus(ref);

    const trimmedKeyword = keyword.trim();
    const isEmpty = trimmedKeyword.length === 0;

    const logAnalyticsEvent = useLogAnalyticsEvent();

    const { mutateAsync: postNonEntryMarkets, isPending } =
      usePostNonEntryMarkets();

    const handleSubmit = useCallback(async () => {
      if (isEmpty || isPending) return;

      try {
        await postNonEntryMarkets({ keyword: trimmedKeyword });

        logAnalyticsEvent({
          name: 'COMPLETE_INPUT_NON_ENTRY_MARKET',
          analytics: {
            ...(searchKeyword != null && { SEARCH_KEYWORD: searchKeyword }),
            TEXT: trimmedKeyword,
            ...logging?.analytics,
          },
          ably_only_log: { ...logging?.ably_only_log },
        });

        Keyboard.dismiss();
        bridge.toast('입점 요청이 완료되었어요');
        confirm();
      } catch (error) {
        const errorMessage =
          error instanceof Error && error.message
            ? error.message
            : '오류가 발생했어요';
        bridge.toast(errorMessage);
      }
    }, [
      isEmpty,
      isPending,
      postNonEntryMarkets,
      trimmedKeyword,
      searchKeyword,
      logging,
      logAnalyticsEvent,
      confirm,
    ]);

    return (
      <KeyboardAvoiding>
        <BottomSheetContainer
          title="찾고 있는 마켓명을 입력해 주세요"
          onPressClose={() => {
            Keyboard.dismiss();
            cancel();
          }}
        >
          <Flex.Column padding={{ horizontal: 16, vertical: 16 }}>
            <Flex
              borderRadius={4}
              borderWidth={1}
              borderColor="border_primary"
              padding={{ horizontal: 12 }}
              height={48}
              justifyContent="center"
            >
              <TextInput
                ref={ref}
                defaultValue={searchKeyword}
                style={{
                  fontSize: 14,
                  outlineWidth: 0,
                  padding: 0,
                }}
                placeholder="마켓명을 입력해 주세요"
                placeholderTextColor={ColorValues.gray50}
                autoFocus={!isWeb}
                onChangeText={setKeyword}
                returnKeyType="search"
                onSubmitEditing={handleSubmit}
                clearButtonMode="while-editing"
              />
            </Flex>
          </Flex.Column>
          <PrimaryButton
            variant="fill"
            color="gray"
            size="xlarge"
            removeBorderRadius={true}
            state={isEmpty ? 'disabled' : isPending ? 'loading' : undefined}
            onPress={handleSubmit}
          >
            입점 요청하기
          </PrimaryButton>
        </BottomSheetContainer>
      </KeyboardAvoiding>
    );
  },
);

/**
 * Android 에서는 autoFocus 만으로는 키보드가 자동으로 올라오지 않아,
 * 마운트 직후 blur → focus 를 재호출하여 키보드를 강제로 노출시킨다.
 */
const useKeyboardFocus = (ref: RefObject<TextInput | null>) => {
  useAsyncEffect(async () => {
    if (!isAndroid) {
      return;
    }
    await delay(100);
    ref.current?.blur();
    ref.current?.focus();
  }, []);
};
